import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertTitle, Box, Button, Skeleton, Typography } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { useUnifiedEngagementReview } from '../../hooks/advisory/useUnifiedEngagementReview';
import {
  hasReviewPreparationFailure,
  isUnifiedReviewEnrollmentEnabled,
} from '../../hooks/advisory/advisoryReview';
import { AdvisoryWorkspaceLive } from './components/workspace/AdvisoryWorkspaceLive';
import { AdvisoryUnifiedReviewLive } from './components/AdvisoryUnifiedReviewLive';
import { AdvisoryReviewRecoveryControls } from './components/AdvisoryReviewRecoveryControls';
import type { RootState } from '../../redux/rootReducer';
import type { AppDispatch } from '../../redux/store';
import {
  deriveAdvisoryStage,
  deriveChannelState,
  EMPTY_CHANNEL_STATUS,
  mapAdvisoryErrorCode,
  startEngagementListeners,
  stopEngagementListeners,
} from '../../redux/slices/advisory';
import type { KeyQuestionDoc } from '../../redux/slices/advisory';
import type { CompanyFact, CounterpartiesFact } from '../../redux/slices/advisory/advisoryTypes';
import { useSupervisorTurn } from '../../hooks/advisory/useSupervisorTurn';
import type {
  SupervisorEngagementState,
  SupervisorTurnRequest,
} from '../../hooks/advisory/useSupervisorTurn';
import { AdvisoryCalloutState } from './components/AdvisoryCalloutState';
import { AdvisoryThinkingState } from './components/AdvisoryThinkingState';
import { AdvisoryGoalReviewScreen } from './components/AdvisoryGoalReviewScreen';
import { AdvisoryForkScreen } from './components/AdvisoryForkScreen';
import { AdvisoryKeyQuestionEditor } from './components/AdvisoryKeyQuestionEditor';
import { AdvisoryEvidenceScreen } from './components/AdvisoryEvidenceScreen';
import { AdvisoryCompanyScreen } from './components/AdvisoryCompanyScreen';
import { AdvisoryPeriodConfirmationScreen } from './components/AdvisoryPeriodConfirmationScreen';
import { AdvisoryCounterpartiesScreen } from './components/AdvisoryCounterpartiesScreen';
import { AdvisoryCounterpartyProfilesScreen } from './components/AdvisoryCounterpartyProfilesScreen';
import { useAdvisoryCompanyFact } from '../../hooks/advisory/useAdvisoryCompanyFact';
import { useAdvisoryPeriodFact } from '../../hooks/advisory/useAdvisoryPeriodFact';
import {
  deriveAdvisoryPeriodView,
  isAdvisoryPeriodConfirmationEnabled,
} from './advisoryPeriodView';
import type { ReportingPeriodV01 } from '../../hooks/advisory/advisoryReportingPeriod';
import { isValidAdvisoryProjectId } from './advisoryPresentation';
import { resolveDocsCollectionId } from '../../redux/slices/advisory/advisoryCollections';
import { useAdvisoryDocuments } from '../../hooks/advisory/useAdvisoryDocuments';
import { AdvisoryRunProgress } from './components/AdvisoryRunProgress';
import { AdvisoryAnswerProgress } from './components/AdvisoryAnswerProgress';
import { runViewFromStage, runViewFromStream, runViewFromTracking } from './advisoryRunView';
import { useAdvisoryRunTracking } from '../../hooks/advisory/useAdvisoryRunTracking';
import { useAdvisoryWizardParams } from './advisoryWizardParams';
import { stepPosition } from './advisoryStepPosition';
import { pageSurface, contentColumn, heading, meta } from '../../styles/visualLanguage';
import { useAdvisoryAnswers } from '../../hooks/advisory/useAdvisoryAnswers';
import { useAdvisoryResearchContext } from '../../hooks/advisory/useAdvisoryResearchContext';
import { AdvisoryBriefDocument } from '../../advisory/composites/ui';
import {
  buildAdvisoryAnswerProgressRows,
  buildLiveAdvisoryBrief,
  bindWorkspaceBriefToRun,
  deriveAdvisoryAnswerLifecycle,
  isAdvisoryLiveAnswersEnabled,
} from './advisoryAnswerView';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';
import { getValidToken } from '../../redux/slices/userSlice';
import { readAdvisorySourcePassage } from '../../services/advisorySourcePassageService';
import { isEngagementEngine } from '../../hooks/advisory/advisoryEngine';
import { readPendingIntake, savePendingIntake } from '../../hooks/advisory/advisoryIntake';
import { AdvisoryEngineLabel } from './components/AdvisoryEngineLabel';

/**
 * AdvisoryEngagementPage — the `/advisory/:projectId` route.
 *
 * Auth guard: duplicates `AdvisoryListPage.tsx`'s inline redirect pattern
 * independently — read, never modified, no shared guard extracted.
 *
 * Stage derivation is composed with no second derivation path:
 * `deriveChannelState` feeds the unchanged `deriveAdvisoryStage`. This is
 * what makes a mid-refinement document land on goal review, never the
 * intake screen.
 *
 * Listener lifecycle: the mount effect is the sole subscription path
 * required, regardless of arrival route — a refresh, a pasted URL or a
 * bookmark has no row click to have subscribed for it.
 *
 * Turn ownership: this page, not `AdvisoryIntakePage`, owns the supervisor
 * write-path hook instance and fires the pending turn. The intake page must
 * not own the turn: it unmounts the instant it navigates away on submit,
 * and a fetch started there would lose its React state updates (safeSet
 * no-ops after unmount). agent-svc writes the goal document during the
 * stream, so the turn must be fired from the engagement page that stays
 * mounted for the lifetime of that turn. `useSupervisorTurn` deliberately
 * does not abort on unmount (StrictMode/Fast Refresh remounts) — only
 * overlapping `send()` calls abort.
 */
export function AdvisoryEngagementPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const deleted = useSelector((state: RootState) =>
    projectId ? state.advisory.deletedEngagementIds.includes(projectId) : false
  );

  // Unmount every detail hook when deletion is confirmed, including listeners
  // for retained child documents and effects that can start another turn.
  return deleted ? <Navigate to="/advisory" replace /> : <ActiveAdvisoryEngagementPage />;
}

function ActiveAdvisoryEngagementPage() {
  const { t } = useUntypedTranslation();
  // The route param is validated HERE, once, at the boundary — every
  // consumer below (the listener effect, the two remove handlers, the
  // editors' `projectId` prop, and through them every Firestore path
  // template in `advisoryService.ts`) receives either a path-safe id or
  // `undefined`, never a raw URL segment. React Router decodes path
  // params, so without this `/advisory/a%2Fb%2Fc` reaches the Firestore
  // SDK as `a/b/c`. See `isValidAdvisoryProjectId` for the full reasoning;
  // a rejected id renders the calm `absent` state below rather than being
  // passed on to the service layer.
  const { projectId: rawProjectId } = useParams<{ projectId: string }>();
  const projectId = isValidAdvisoryProjectId(rawProjectId) ? rawProjectId : undefined;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    isAuthenticated,
    loading: userLoading,
  } = useSelector((state: RootState) => state.user);
  const entry = useSelector((state: RootState) =>
    projectId ? state.advisory.byProjectId[projectId] : undefined
  );
  const {
    review: sseReview = null,
    reviewProgress = null,
    status: turnStatus,
    progressPhase,
    errorI18nKey,
    goal: sseGoal,
    engagement: sseEngagement,
    questions: sseQuestions,
    // Defaulted here (never in the hook, which always returns a real `{}`)
    // purely as a test-mock safety net — several existing
    // `(useSupervisorTurn as jest.Mock).mockReturnValue({...})` call sites
    // in this page's own test file predate this field and omit it, which
    // would otherwise reach `Object.keys(undefined)` downstream.
    orphaned: sseOrphaned = {},
    proposals: sseProposals = null,
    // Defaulted like its siblings above, because `useSupervisorTurn` is
    // jest.mock'd in this page's tests and older mocks return neither field.
    analysis = null,
    answers = null,
    // Defaulted for the same jest-mock reason as their siblings above.
    company: sseCompany = null,
    companyConfirmed: sseCompanyConfirmed = null,
    period: ssePeriod = null,
    periodConfirmed: ssePeriodConfirmed = null,
    counterparties: sseCounterparties = null,
    counterpartiesConfirmed: sseCounterpartiesConfirmed = null,
    counterpartyProfiles: sseCounterpartyProfiles = null,
    send,
  } = useSupervisorTurn({
    identityKey: isAuthenticated && user?.id && projectId ? `${user.id}/${projectId}` : undefined,
  });
  const lastRequestRef = useRef<SupervisorTurnRequest | null>(null);
  const firedForRef = useRef<string | null>(null);
  const requestScope = `${isAuthenticated ? user?.id ?? '' : ''}/${projectId ?? ''}`;
  const requestScopeRef = useRef(requestScope);
  if (requestScopeRef.current !== requestScope) {
    requestScopeRef.current = requestScope;
    lastRequestRef.current = null;
    firedForRef.current = null;
  }
  const intakeState = location.state as { userQuery?: string; engine?: unknown } | null;
  const savedIntake = useMemo(
    () => (isAuthenticated && user?.id && projectId ? readPendingIntake(requestScope) : null),
    [requestScope, isAuthenticated, user?.id, projectId]
  );
  const pendingQuery = savedIntake?.userQuery ?? intakeState?.userQuery ?? null;
  const intakeEngine = intakeState?.engine;
  const pendingEngine = savedIntake
    ? savedIntake.engine
    : isEngagementEngine(intakeEngine)
    ? intakeEngine
    : undefined;
  const [intakeStorageError, setIntakeStorageError] = useState<string | null>(null);
  const [intakeAttempt, setIntakeAttempt] = useState(0);
  // Clearing router state must not erase the submitted decision while the
  // engagement route owns the request. Scope it to prevent cross-project leaks.
  const submittedQuery = useRef({
    scope: requestScope,
    query: pendingQuery || '',
    engine: pendingEngine,
    fresh: Boolean(pendingQuery),
  });
  if (submittedQuery.current.scope !== requestScope)
    submittedQuery.current = {
      scope: requestScope,
      query: pendingQuery || '',
      engine: pendingEngine,
      fresh: Boolean(pendingQuery),
    };
  if (pendingQuery) {
    submittedQuery.current.query = pendingQuery;
    submittedQuery.current.engine = pendingEngine;
  }
  const enrollReview = isUnifiedReviewEnrollmentEnabled();
  const sendReview = useCallback(
    (request: SupervisorTurnRequest) => {
      if (requestScopeRef.current === requestScope && request.projectId === projectId)
        lastRequestRef.current = request;
      return send(request);
    },
    [send, requestScope, projectId]
  );
  const unifiedReview = useUnifiedEngagementReview({
    userId: isAuthenticated ? user?.id : undefined,
    projectId,
    send: sendReview,
    liveReview: sseReview,
  });
  const executeReview = unifiedReview.execute;
  const usesUnifiedReview = Boolean(
    entry?.goalDoc?.status === 'review' ||
      entry?.goalDoc?.reviewWorkflowVersion === 1 ||
      unifiedReview.snapshot ||
      (lastRequestRef.current?.projectId === projectId && lastRequestRef.current?.review) ||
      (pendingQuery && (enrollReview || savedIntake))
  );
  const reviewPresentation = {
    initialQuestion:
      submittedQuery.current.query ||
      unifiedReview.snapshot?.originalQuery ||
      entry?.goalDoc?.originalQuery ||
      '',
    progress: reviewProgress,
    animateEntry: submittedQuery.current.fresh,
  };
  const usesWorkspace = entry?.goalDoc?.workspaceVersion === 1;
  const answersStartForRef = useRef<string | null>(null);
  // Read once per render. path/step are only honoured in the `confirmed`
  // case below; earlier branches derive their content from `stage` alone.
  const { path, step, setWizardParams } = useAdvisoryWizardParams();

  // Redirecting during auth bootstrap would bounce a signed-in user off
  // their own page, so this waits for `userLoading` to finish.
  useEffect(() => {
    if (!userLoading && !isAuthenticated) {
      navigate('/signin');
    }
  }, [isAuthenticated, userLoading, navigate]);

  // This effect's dependency array is deliberately [dispatch, user?.id,
  // projectId] ONLY — a `location.search` change from the wizard params
  // (choosing a path, moving between steps) must NEVER re-register these
  // listeners. Do not "fix" this by adding location.search; that would tear
  // down and rebuild a live Firestore subscription on every fork/step click.
  useEffect(() => {
    if (!user?.id || !projectId) {
      return;
    }
    const userId = user.id;
    dispatch(startEngagementListeners({ userId, projectId }));
    return () => {
      dispatch(stopEngagementListeners({ userId, projectId }));
    };
  }, [dispatch, user?.id, projectId]);

  // Fires the pending turn carried from the intake screen's router state
  // exactly once per user/project scope. `firedForRef` is what makes this
  // idempotent across a re-render triggered only by a `location.search`
  // change (the wizard params) — a turn already fired for this scope
  // is never re-fired.
  //
  // Defer send+navigate to a macrotask so React 18 StrictMode's
  // mount→cleanup→mount cycle cancels the first schedule instead of
  // aborting an in-flight fetch. Without this, the first send is aborted
  // by useSupervisorTurn's unmount cleanup, firedForRef is already set,
  // and the remount never retries — submit logs, no HTTP response ever.
  useEffect(() => {
    if (
      !isAuthenticated ||
      userLoading ||
      !user?.id ||
      !projectId ||
      !pendingQuery ||
      firedForRef.current === requestScope
    ) {
      return;
    }

    const request: SupervisorTurnRequest =
      enrollReview || savedIntake
        ? {
            projectId,
            review: readPendingIntake(requestScope) ?? {
              operation: 'prepare',
              requestId: uuidv4(),
              userQuery: pendingQuery,
              workspaceVersion: 1,
              ...(pendingEngine === undefined ? {} : { engine: pendingEngine }),
            },
          }
        : { projectId, userQuery: pendingQuery };
    if (request.review?.operation === 'prepare') {
      try {
        savePendingIntake(requestScope, request.review);
        setIntakeStorageError(null);
      } catch {
        setIntakeStorageError(requestScope);
        return;
      }
    }
    lastRequestRef.current = request;

    const timer = window.setTimeout(() => {
      if (requestScopeRef.current !== requestScope || firedForRef.current === requestScope) {
        return;
      }
      firedForRef.current = requestScope;
      if (request.review) void executeReview(request.review);
      else void send(request);
      // The saved intake payload keeps the exact receipt across refreshes until
      // the server acknowledges it. Only then can recovery use server state.
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    projectId,
    pendingQuery,
    pendingEngine,
    savedIntake,
    intakeAttempt,
    location.pathname,
    location.search,
    navigate,
    send,
    enrollReview,
    executeReview,
    isAuthenticated,
    userLoading,
    user?.id,
    requestScope,
  ]);

  const turnInFlight = turnStatus === 'sending' || turnStatus === 'streaming';

  // Whether the turn currently in flight, or the turn that just failed,
  // belongs to the confirm/classify family. Both run entirely inside the
  // confirm-turn's own stream (goalConfirmed -> engagement -> questions ->
  // done, measured at 2.81-2.85s end to end) — a fundamentally shorter,
  // more predictable wait than the 5-25s ask/refine goal-extraction
  // silence, and a failure arriving after either request means the goal is
  // already confirmed server-side. Read from lastRequestRef.current —
  // LOCAL turn context, never inferred from the SSE `error` event's own
  // content, which carries no field distinguishing a classification failure
  // from any other terminal error.
  const isConfirmFamilyTurn =
    Boolean(lastRequestRef.current?.confirm) || Boolean(lastRequestRef.current?.classify);

  // Whether the turn currently in flight, or the turn that just failed, is
  // a key-question removal. Removal reuses the SAME send()/turn machinery
  // as every other request (there is exactly one permitted call site), but
  // it must NOT swap the whole page over to the full-screen thinking/failure
  // treatment the way ask/refine/confirm/classify do: the key-question
  // editor already has its own inline in-flight indicator (the clicked
  // card's `removingId` state) and inline error echo
  // (`removeErrorI18nKey`), and unmounting it mid-removal would discard
  // both for no benefit — there is no second async phase to narrate,
  // removal turns measure well under a second (`removeOne`/`removeAll`
  // fixtures: 0.55-0.56s end to end). See the `turnStatus === 'error'` and
  // `turnInFlight` branches below, both of which exclude this turn kind.
  const isRemovalTurn = Boolean(lastRequestRef.current?.removeQuestions);
  const isAddTurn = Boolean(lastRequestRef.current?.addQuestion);
  const isAcceptTurn = Boolean(lastRequestRef.current?.acceptQuestion);
  const isInlineEditorTurn =
    isRemovalTurn || isAddTurn || isAcceptTurn || Boolean(lastRequestRef.current?.review);
  const isPeriodConfirmTurn = Boolean(
    lastRequestRef.current?.confirmPeriod || lastRequestRef.current?.useCurrentYear
  );

  // A classification failure re-runs classification only —
  // `{projectId, classify: true}` against the SAME confirmed goal, never
  // `{confirm: true}` again (the backend rejects re-confirming an
  // already-confirmed goal). Mints its own request and records it on
  // lastRequestRef so a second consecutive classification failure retries
  // the same way.
  const handleRetryClassification = () => {
    if (!projectId) {
      return;
    }
    const request: SupervisorTurnRequest = { projectId, classify: true };
    lastRequestRef.current = request;
    void send(request);
  };

  // A failed supervisor turn retries by re-firing that identical turn —
  // EXCEPT a confirm-family failure (see handleRetryClassification above),
  // which retries via handleRetryClassification instead of re-sending the
  // stale {confirm: true} request. `send()` aborts any in-flight turn itself, so
  // no manual abort precedes either path. When no turn has ever fired in
  // this tab (arrival by reload or bookmark), there is no identical turn to
  // re-fire, so no retry is offered.
  const retryTurn = lastRequestRef.current
    ? isConfirmFamilyTurn
      ? handleRetryClassification
      : () => {
          void send(lastRequestRef.current!);
        }
    : undefined;

  // Re-registering the listeners is idempotent, so this retry needs no
  // guard and no teardown first.
  const retryChannel = () => {
    if (user?.id && projectId) {
      dispatch(startEngagementListeners({ userId: user.id, projectId }));
    }
  };

  // refine and confirm are minted in this component — the same instance
  // that owns useSupervisorTurn() — and each records its own request on
  // lastRequestRef before firing, so retryTurn re-fires whichever of the
  // two actually failed. Neither stores or awaits the promise send()
  // returns; the thinking-state branch above (turnInFlight || stage.tag
  // === 'extracting') is what puts the user on the thinking state until
  // the turn resolves, uniformly for both. The resulting new goal arrives
  // from useSupervisorTurn()'s own parsed `goal` state the instant the SSE
  // frame lands, not by waiting on the Firestore listener to catch up —
  // see `sseGoal` below.
  const handleRefine = (feedback: string) => {
    if (!projectId) {
      return;
    }
    const request: SupervisorTurnRequest = { projectId, refinement: feedback };
    lastRequestRef.current = request;
    void send(request);
  };

  const handleConfirm = () => {
    if (!projectId) {
      return;
    }
    const request: SupervisorTurnRequest = { projectId, confirm: true };
    lastRequestRef.current = request;
    void send(request);
  };

  /**
   * The company step's terminal action — S-04c.
   *
   * `name` is undefined when the user accepted the reading untouched, and the
   * request omits `companyName` in that case rather than echoing the proposal
   * back: the server stores `source: extracted` for an accepted proposal and
   * `source: user` for one typed over, and that distinction is the only
   * measurement of how well the S-02a reading works. A client that always sent
   * the name would make every project look like a correction.
   *
   * Awaited, not fire-and-forget: the navigation to the dropzone is only
   * correct once the confirmation has actually landed. A refused turn leaves
   * the user on this step with the error the turn already surfaced, which is
   * the right place to be — the server refuses `startAnalysis` on an
   * unconfirmed company anyway.
   */
  const handleConfirmCompany = async (name?: string): Promise<void> => {
    if (!projectId) {
      return;
    }
    const request: SupervisorTurnRequest = {
      projectId,
      confirmCompany: true,
      ...(name ? { companyName: name } : {}),
    };
    lastRequestRef.current = request;
    const outcome = await send(request);
    if (outcome.status !== 'done') {
      // The turn's own error is already surfaced, and staying on this screen is
      // right: nothing after it works without a subject.
      return;
    }
    // No navigation. The confirmation marks `facts/company` confirmed, and
    // re-reading it is what makes `deriveAdvisoryStage` stop returning this
    // stage — the goal review is then simply what renders next. A one-shot fact
    // read has no listener to deliver the change, so the refetch IS the update.
    refetchCompanyFact();
  };

  const handleConfirmPeriod = (period?: ReportingPeriodV01) => {
    if (!projectId) {
      return Promise.resolve({ status: 'idle' as const, errorI18nKey: null });
    }
    const request: SupervisorTurnRequest = {
      projectId,
      confirmPeriod: true,
      period,
    };
    lastRequestRef.current = request;
    return send(request);
  };

  /**
   * The counterparty screen's terminal action — S-03a.
   *
   * Sends the WHOLE curated list rather than a diff: two to six names edited in
   * one screen are not worth a per-row round trip, and one array write cannot
   * half-apply the way a delete plus an add can.
   *
   * No navigation on success, for the same reason as the company step: the
   * confirmation marks `facts/counterparties` confirmed, and re-reading it is
   * what makes `deriveAdvisoryStage` stop returning this stage.
   */
  const handleConfirmCounterparties = async (companies: string[]): Promise<void> => {
    if (!projectId) {
      return;
    }
    const request: SupervisorTurnRequest = {
      projectId,
      confirmCounterparties: true,
      counterparties: companies,
    };
    lastRequestRef.current = request;
    const outcome = await send(request);
    if (outcome.status !== 'done') {
      return;
    }
    refetchCompanyFact();
  };

  /**
   * Retry ONE counterparty's profile, optionally from a URL the user supplies.
   *
   * The URL is the whole point of the affordance: a company too new, too small
   * or too private to be found by name is exactly where one page turns an empty
   * result into a profile, and that is knowledge the user has and the research
   * does not.
   *
   * Server-side this is cheap where it can be — a profile whose research
   * succeeded and whose ingestion failed is re-ingested from the stored prose
   * rather than researched again.
   */
  const handleRetryCounterpartyProfile = async (company: string, url?: string): Promise<void> => {
    if (!projectId) {
      return;
    }
    // A retry is new live work, so whatever the user acknowledged before it is
    // stale. Without this reset, a `Continue` followed by a retry from a second
    // tab would leave this one refusing to show the run it started.
    setProfileAcknowledgementScope(null);
    const request: SupervisorTurnRequest = {
      projectId,
      retryCounterpartyProfile: true,
      counterparty: company,
      ...(url ? { counterpartyUrl: url } : {}),
    };
    lastRequestRef.current = request;
    const outcome = await send(request);
    if (outcome.status !== 'done') {
      return;
    }
    // No listener behind the fact document, so re-reading IS the update.
    refetchCompanyFact();
  };

  /**
   * Leave the profile screen with the gaps unfilled.
   *
   * A profile that cannot be found is a permanent gap the answering stage
   * reports as missing evidence, so this is an offer rather than a toll — the
   * alternative is stranding the user on a screen whose retry may never work.
   *
   * Clearing the wizard params is NOT what advances the stage, and on its own it
   * did nothing at all: the stage is derived from `facts/counterparties`, a
   * terminal failure never leaves that document, so the click stripped the URL
   * and re-derived the very same research screen. `profilesAcknowledged` is the
   * part that releases it — see the gate in `deriveAdvisoryStage`. Both are
   * needed: the flag decides the stage, the navigate stops a stale wizard param
   * from putting the user back on a step they have left.
   */
  const handleContinuePastProfiles = (): void => {
    setProfileAcknowledgementScope(requestScope);
    navigate(location.pathname, { replace: true });
  };

  /**
   * The evidence screen's terminal action — S-05. Two turns, in this order and
   * never merged, because the ORDER is the whole point:
   *
   * `confirmQuestions` locks the set. That lock is the gate agent-svc waits on;
   * without it `startAnalysis` is refused. It is idempotent, so sending it on a
   * set already locked reports the lock rather than failing — which is what makes
   * it safe to send unconditionally here rather than tracking whether the user
   * already locked it.
   *
   * `startAnalysis` then answers the set. It streams for the length of the
   * engagement — roughly thirteen minutes on the default debug shape — and
   * re-sending it re-attaches to a run already in flight instead of starting a
   * second engagement, because every V3 turn is a detached Cloud Run Job.
   *
   * `prepareAnalysis` is deliberately NOT sent from here. It publishes the
   * docintel handle a document has to be uploaded into, and it has to happen
   * BEFORE any upload — a node reads its collection as it stands when its turn
   * runs, so a file arriving mid-run is a file the engagement never sees. Firing
   * it at the same instant as the run would make the upload window zero-width.
   *
   * Awaited rather than fire-and-forget, unlike every other handler on this page:
   * the second turn is only valid once the first has settled, and `send()` aborts
   * any in-flight turn, so two overlapping calls would abort the lock.
   */
  const handleStartAnalysis = async (): Promise<void> => {
    if (!projectId) {
      return;
    }
    const lockRequest: SupervisorTurnRequest = { projectId, confirmQuestions: true };
    lastRequestRef.current = lockRequest;
    const lock = await send(lockRequest);
    if (lock.status !== 'done') {
      // THROWS rather than returning. Returning normally here left the evidence
      // screen's `handoff` flag set, so a refused lock stranded the user on a
      // full-page wait that could never end — the screen resets that flag from a
      // rejection, and only from a rejection. Starting a run on an unlocked set
      // would just be refused server-side anyway, and the error the lock turn
      // already surfaced is the one worth showing.
      //
      // The message is a fixed string, never `lock.errorI18nKey` and never
      // provider text: nothing renders it, and the screen must not start seeing
      // error content it has no business holding.
      throw new Error('supervisor: the question set could not be locked');
    }
    // THE profile decision, and it is why this handler needs the document state.
    //
    // Simon's configuration puts `doc_intel` on `sein` and `output_2`, and the
    // supervisor exposes that as `simon-docintel`; the plain `simon` profile has no
    // doc-intel node at all. Both wrong answers are silent:
    //
    //   simon         + documents  -> ingested, stored, paid for, and never read, by
    //                                a run that completes and looks healthy.
    //   simon-docintel + nothing   -> the agent falls back to the conversation's own
    //                                node outputs, measured 2-6x slower and
    //                                truncating 3 of 6 node-runs against 1 of 27.
    //
    // So neither is right in general, and this client is the only party that knows
    // which applies — it watched the upload. `readyCount` is the whole decision, and
    // only `ready` counts: a failed document is not in the collection.
    const runRequest: SupervisorTurnRequest = {
      projectId,
      startAnalysis: true,
      analysisNodeProfile: documents.nodeProfile,
    };
    lastRequestRef.current = runRequest;
    void send(runRequest);
  };

  // Entries are created lazily on the slice, so `entry` can be undefined on
  // first render — default the channel so the first render derives
  // 'loading' rather than throwing.
  const goalChannel = entry?.channels.goal ?? { ...EMPTY_CHANNEL_STATUS };
  const channelState = deriveChannelState(goalChannel);

  // The docintel collection to upload into.
  //
  // Derived from the project id — which IS the conversation id, because a project
  // runs exactly one engagement and agent-svc defaults it that way at both mint
  // sites. That is what makes an upload possible before anything has been prepared
  // and before any turn exists, which is the only ordering that works: a node reads
  // its collection as it stands when its turn runs.
  //
  // A stored handle still wins. A project prepared before that default carries a
  // server-minted uuid4, and its already-uploaded documents live under the handle
  // derived from THAT — deriving would point the upload at a different, empty
  // collection and the files would simply never be read.
  const storedDocsCollectionId = entry?.goalDoc?.analysis?.docsCollectionId ?? null;
  const docsCollectionId = projectId
    ? resolveDocsCollectionId(projectId, storedDocsCollectionId)
    : null;
  const documents = useAdvisoryDocuments(docsCollectionId);
  // `facts/company` is a subcollection document, so the goal channel's listener
  // never sees it — it is read separately and handed to the derivation.
  const {
    company: companyFact,
    counterparties: counterpartiesFact,
    refetch: refetchCompanyFact,
  } = useAdvisoryCompanyFact(user?.id, projectId);
  // The profile screen's `Continue`, held here rather than in the document: it
  // is this tab's acknowledgement of a permanent evidence gap it has shown, and
  // there is no server field for it. Scope it to the account and project so
  // another engagement cannot inherit consent, even before effects run.
  const [profileAcknowledgementScope, setProfileAcknowledgementScope] = useState<string | null>(
    null
  );
  const profilesAcknowledged = profileAcknowledgementScope === requestScope;
  useEffect(() => {
    setProfileAcknowledgementScope(null);
  }, [requestScope]);

  // THIS TAB'S OWN STREAM WINS over the fetched document, and that ordering is
  // the whole reason the question appears at all on a first turn.
  //
  // The fact is created mid-stream, by the same turn that extracts the goal. The
  // hook fetched it when the page mounted — which on a brand-new project is
  // BEFORE the document exists — so it reads `null`, gates nothing, and the goal
  // review renders straight past the company question. The SSE `company` event
  // is the only thing that knows, and it arrives seconds later with no listener
  // behind it to deliver the write.
  //
  // `companyConfirmed` outranks `company` because the confirm turn emits only the
  // former, and the refetch that would otherwise carry the news lands a moment
  // later — without this the screen re-renders itself once after the click.
  const approvedReview = unifiedReview.approved ? unifiedReview.snapshot : null;
  const liveCompany: CompanyFact | null | undefined = approvedReview
    ? { companyName: approvedReview.values.subjectCompany, confirmed: true, error: '' }
    : sseCompanyConfirmed
    ? { companyName: sseCompanyConfirmed.companyName, confirmed: true, error: '' }
    : sseCompany
    ? {
        companyName: sseCompany.companyName,
        confirmed: sseCompany.confirmed,
        // The stage reads this as a boolean; the string is never rendered.
        error: sseCompany.extractionFailed ? 'extraction_failed' : '',
      }
    : companyFact;
  // Same precedence, same reason: the list is written mid-stream by the confirm
  // turn that classifies the engagement, so a fetch fired at mount saw nothing.
  //
  // `profiles` always comes from the FETCHED document, never from the stream.
  // The stream reports the list being confirmed; the profiles are written per
  // company as the research lands, and the fact read is what has them. Making
  // one up here would show an empty research screen while the document already
  // said two companies were done.
  const liveCounterparties: CounterpartiesFact | null | undefined =
    approvedReview?.counterpartyKind && !counterpartiesFact?.confirmed
      ? {
          companies: approvedReview.values.counterparties,
          kind: approvedReview.counterpartyKind,
          confirmed: true,
          error: '',
          profiles: counterpartiesFact?.profiles ?? {},
          dropped: counterpartiesFact?.dropped ?? [],
        }
      : sseCounterpartiesConfirmed
      ? {
          companies: sseCounterpartiesConfirmed.companies,
          kind: sseCounterpartiesConfirmed.kind,
          confirmed: true,
          error: '',
          profiles: counterpartiesFact?.profiles ?? {},
          dropped: counterpartiesFact?.dropped ?? [],
        }
      : sseCounterparties
      ? {
          companies: sseCounterparties.companies,
          kind: sseCounterparties.kind,
          confirmed: sseCounterparties.confirmed,
          // The stage reads this as a boolean; the string is never rendered.
          error: sseCounterparties.extractionFailed ? 'extraction_failed' : '',
          profiles: counterpartiesFact?.profiles ?? {},
          dropped: counterpartiesFact?.dropped ?? [],
        }
      : counterpartiesFact;

  const stage = deriveAdvisoryStage(
    channelState,
    entry?.goalDoc ?? null,
    goalChannel.errorCode,
    liveCompany,
    liveCounterparties,
    // The live overlay. It is what makes the research screen appear on the
    // confirm turn itself: the fact listener attaches at mount, before the list
    // exists, and the stream is ahead of the first snapshot for that turn.
    sseCounterpartyProfiles,
    profilesAcknowledged
  );

  // Profile rows are created after question initialization. Once those rows
  // settle, Continue can acknowledge their gaps without changing the failed
  // preparation state or discarding the checkpoints needed for a later retry.
  // Pending rows still hold the profile screen in deriveAdvisoryStage above.
  const profilePreparationFailed = approvedReview?.downstream.status === 'failed';
  const acknowledgedProfileGaps = profilePreparationFailed && profilesAcknowledged;
  const isProfilePreparationError =
    profilePreparationFailed &&
    Boolean(lastRequestRef.current?.retryCounterpartyProfile) &&
    (stage.tag === 'researchingCounterparties' || acknowledgedProfileGaps);

  const periodConfirmationEnabled = isAdvisoryPeriodConfirmationEnabled();
  const periodFact = useAdvisoryPeriodFact({
    enabled: periodConfirmationEnabled && Boolean(entry?.goalDoc),
    userId: user?.id,
    projectId,
    confirmedFactPath: ssePeriodConfirmed?.factPath,
  });
  const periodView = deriveAdvisoryPeriodView({
    enabled: periodConfirmationEnabled,
    userId: user?.id,
    projectId,
    project: entry?.goalDoc ?? null,
    livePeriod: ssePeriod,
    liveConfirmation:
      approvedReview && user?.id && projectId
        ? {
            period: approvedReview.values.period,
            factPath: `users/${user.id}/strategic_advisory/${projectId}/facts/period`,
          }
        : ssePeriodConfirmed,
    factStatus: periodFact.status,
    fact: periodFact.fact,
  });

  // Re-read the fact once the run settles, so the rows switch from this tab's
  // narration to the stored record — which is the only thing that carries
  // `sourceUrl` for a retry, and the only thing a reload can recover.
  const profilesCompleted =
    sseCounterpartyProfiles?.completed !== undefined && sseCounterpartyProfiles?.completed !== null;
  useEffect(() => {
    if (profilesCompleted) {
      refetchCompanyFact();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profilesCompleted]);

  const liveAnswersEnabled = usesWorkspace || isAdvisoryLiveAnswersEnabled();
  const advisoryAnswers = useAdvisoryAnswers({
    enabled: liveAnswersEnabled,
    userId: user?.id,
    projectId,
    durableRunId: entry?.goalDoc?.answers?.runId || undefined,
    liveRun: answers,
  });
  const answerLifecycle = deriveAdvisoryAnswerLifecycle(
    entry?.goalDoc?.answers,
    advisoryAnswers.orchestrationDoc,
    answers,
    advisoryAnswers.stoppedWriting
  );
  const readsResearchContext =
    liveAnswersEnabled &&
    (answerLifecycle.tag === 'completed' ||
      answerLifecycle.tag === 'partial' ||
      answerLifecycle.tag === 'failed' ||
      answerLifecycle.tag === 'stale');
  const advisoryResearchContext = useAdvisoryResearchContext({
    enabled: readsResearchContext,
    userId: user?.id,
    rootTurnId: entry?.goalDoc?.analysis?.rootTurnId || undefined,
  });

  // S-05 -> S-06 is automatic. The macrotask is load-bearing under StrictMode:
  // the simulated first effect mount is cancelled before a request is sent.
  useEffect(() => {
    const analysisStatus = entry?.goalDoc?.analysis?.status;
    const eligibleAnalysis = analysisStatus === 'completed' || analysisStatus === 'partial';
    const s05Settled = turnStatus !== 'sending' && turnStatus !== 'streaming';
    if (
      usesWorkspace ||
      !liveAnswersEnabled ||
      !projectId ||
      !eligibleAnalysis ||
      !s05Settled ||
      entry?.goalDoc?.answers ||
      answers ||
      answersStartForRef.current === projectId
    ) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      if (answersStartForRef.current === projectId) return;
      answersStartForRef.current = projectId;
      const request: SupervisorTurnRequest = {
        projectId,
        startAnswers: true,
        answersRerunMode: 'resume',
      };
      lastRequestRef.current = request;
      void send(request);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [
    answers,
    entry?.goalDoc?.analysis?.status,
    entry?.goalDoc?.answers,
    liveAnswersEnabled,
    usesWorkspace,
    projectId,
    send,
    turnStatus,
  ]);

  // The run, from whichever source can see it — in order of what each can know.
  //
  // The run id comes from the STREAM while this client is the one running it, and
  // from the project DOCUMENT otherwise, which is what makes the tracking listener
  // work after a reload as well as during a live run.
  const activeRunId = analysis?.runId || entry?.goalDoc?.analysis?.runId || undefined;
  const tracking = useAdvisoryRunTracking(user?.id, projectId, activeRunId);

  // The key-questions channel, derived the same defensive way as the goal
  // channel above.
  const keyQuestionsChannel = entry?.channels.keyQuestions ?? { ...EMPTY_CHANNEL_STATUS };
  const keyQuestionsChannelState = deriveChannelState(keyQuestionsChannel);

  // Prefers this browser tab's own live SSE `questions` state over the
  // Firestore-fed `entry?.keyQuestions` — mirroring sseGoal's own fallback
  // reasoning above. Only the four fields `KeyQuestionDoc` models are
  // carried across (`order` added by a render-order fix — the server's own
  // field, carried through unchanged, never re-derived here). Falls back
  // to the pre-existing onSnapshot-fed array when no SSE `questions` event
  // has landed in this tab, not a new reload-recovery read. See
  // `handleRemoveKeyQuestion` below for how the remove flow sources ITS
  // array (a separate, later correction).
  const displayedKeyQuestions = useMemo<KeyQuestionDoc[]>(
    () =>
      sseQuestions && sseQuestions.length > 0
        ? sseQuestions.map(q => ({
            id: q.id,
            question: q.question,
            subquestions: q.subquestions,
            order: q.order,
          }))
        : entry?.keyQuestions ?? [],
    [sseQuestions, entry?.keyQuestions]
  );

  // 1. tracking      — live AND durable: re-rolled after every turn, and readable by
  //                    a client that reloaded or never started the run.
  // 2. stream        — equally live, but private to the client that started it. It
  //                    covers the window before the tracking document's first write.
  // 3. project stage — last resort. It knows a run EXISTS and, mid-run, nothing about
  //                    how far along it is (see `runViewFromStage`), so it reports no
  //                    count rather than the stale "0 of 7" it used to.
  //
  // Derived here, below `displayedKeyQuestions`, rather than beside the tracking
  // listener above: the stream source has no question text of its own, so it reads
  // the same confirmed set the question editor renders. The tracking source needs
  // nothing passed — the run document carries the plan agent-svc wrote for it.
  const trackingRunView = runViewFromTracking(tracking.runDoc, tracking.turnDocs);
  const streamRunView = runViewFromStream(analysis, displayedKeyQuestions);
  const stageRunView = runViewFromStage(stage);
  const runView = trackingRunView ?? streamRunView ?? stageRunView;

  // Live SSE data must not be starved by the Firestore keyQuestions
  // channel's own 'loading' window — that channel's listener can still be
  // waiting on its first snapshot in the same instant the confirm turn's
  // SSE stream already delivered every key question. Once SSE has data,
  // the editor renders it as 'ready' regardless of the real channel state;
  // with no SSE data (e.g. a reload with no live turn in this tab), the
  // real Firestore channel state governs exactly as before.
  const effectiveKeyQuestionsChannelState =
    sseQuestions && sseQuestions.length > 0 ? 'ready' : keyQuestionsChannelState;

  const answerProgressRows = buildAdvisoryAnswerProgressRows(
    answers,
    advisoryAnswers.orchestrationDoc,
    advisoryAnswers.runId ?? '',
    displayedKeyQuestions,
    advisoryAnswers.answerDocuments
  );
  const briefGoal = entry?.goalDoc;
  const liveBrief = useMemo(
    () =>
      briefGoal
        ? buildLiveAdvisoryBrief(
            briefGoal,
            briefGoal.engagement,
            displayedKeyQuestions,
            advisoryAnswers.answerDocuments,
            {
              context: advisoryResearchContext.context,
              status: advisoryResearchContext.status,
            }
          )
        : { model: null, issues: [] as readonly string[] },
    [
      briefGoal,
      displayedKeyQuestions,
      advisoryAnswers.answerDocuments,
      advisoryResearchContext.context,
      advisoryResearchContext.status,
    ]
  );

  // The confirm-turn's classified engagement, restored from the goal
  // document's own `engagement` sub-object when no live SSE turn has fired
  // in this tab — e.g. a reload landing on the fork screen after
  // confirmation already completed in an earlier tab/session. Mirrors
  // sseGoal's own precedence above: the live SSE value always wins when
  // present, this is a fallback for its absence only, never a second
  // channel this page prefers.
  //
  // Only a `status: 'classified'` sub-object is treated as a real result.
  // `classifying` (the confirm-turn's own classification step may still be
  // running server-side at the instant of reload — the goal listener above
  // will deliver the follow-up write the moment it lands, the identical
  // mechanism that already carries an `extracting` reload through to
  // `awaiting_confirmation`) and `engagement_failed` both resolve to `null`
  // here — the same honest absence the fork screen already renders for "no
  // live turn yet" (AdvisoryForkScreen.tsx). This must never render as a
  // classification that succeeded when it did not, and never render the raw
  // `error` field `mapEngagementDoc` deliberately never surfaces.
  const docEngagement = entry?.goalDoc?.engagement;
  const displayedEngagement: SupervisorEngagementState | null =
    sseEngagement ??
    (docEngagement && docEngagement.status === 'classified'
      ? {
          engagementId: docEngagement.engagementId,
          name: docEngagement.name,
          description: docEngagement.description,
          confidence: docEngagement.confidence,
          rationale: docEngagement.rationale,
          alternatives: docEngagement.alternatives,
        }
      : null);

  // Removal now goes through the SAME send() turn every other request uses
  // — never a Firestore deleteDoc, which the live rules deny by design
  // (see advisoryService.ts's own former-site comment on the deleted
  // `deleteKeyQuestion`). The rendered set is never spliced locally here:
  // a successful removal's new list arrives on the very next `sseQuestions`
  // state this hook exposes — the server's own `questions` response payload,
  // recompacted `order` and all — which `displayedKeyQuestions` above
  // already prefers over the Firestore-fed fallback. This handler's only
  // job is reporting the settled outcome back to the editor's existing
  // inline UI (`removingId`/`removeErrorI18nKey`); it deliberately never
  // narrates the removal onto the page-level turnInFlight/error branches
  // below — see `isRemovalTurn` above for why. Missing `projectId` resolves
  // to `null` (no-op) rather than throwing, mirroring every other handler's
  // guard shape on this page.
  const handleRemoveKeyQuestion = async (keyQuestion: KeyQuestionDoc): Promise<string | null> => {
    if (!projectId) {
      return null;
    }
    const request: SupervisorTurnRequest = { projectId, removeQuestions: [keyQuestion.id] };
    lastRequestRef.current = request;
    const outcome = await send(request);
    return outcome.status === 'error' ? outcome.errorI18nKey : null;
  };

  const handleAddKeyQuestion = async (questionText: string): Promise<string | null> => {
    if (!projectId) {
      return null;
    }
    const request: SupervisorTurnRequest = { projectId, addQuestion: questionText };
    lastRequestRef.current = request;
    const outcome = await send(request);
    return outcome.status === 'error' ? outcome.errorI18nKey : null;
  };

  const handleAcceptQuestion = async (proposalId: string): Promise<string | null> => {
    if (!projectId) {
      return null;
    }
    const request: SupervisorTurnRequest = { projectId, acceptQuestion: proposalId };
    lastRequestRef.current = request;
    const outcome = await send(request);
    return outcome.status === 'error' ? outcome.errorI18nKey : null;
  };

  const hasAnswerResult =
    answerLifecycle.tag === 'completed' ||
    answerLifecycle.tag === 'partial' ||
    answerLifecycle.tag === 'failed' ||
    answerLifecycle.tag === 'stale';
  // Share read errors and incomplete-result warnings across both answer layouts.
  const warningKey =
    answerLifecycle.tag === 'partial'
      ? 'advisory:answers.partialWarning'
      : answerLifecycle.tag === 'failed'
      ? 'advisory:answers.failedWarning'
      : answerLifecycle.tag === 'stale'
      ? 'advisory:answers.staleWarning'
      : null;
  const answerResultPanel = (brief = liveBrief) => (
    <Box sx={{ width: '100%' }}>
      {warningKey ? (
        <Alert severity="warning" data-testid="advisory-answer-run-warning" sx={{ margin: 2 }}>
          <AlertTitle>{t('advisory:answers.incompleteTitle')}</AlertTitle>
          {t(warningKey)}
        </Alert>
      ) : null}
      {advisoryAnswers.answersErrorCode ? (
        <Alert severity="error" sx={{ margin: 2 }}>
          {t('advisory:answers.contentReadFailed')}
        </Alert>
      ) : null}
      {brief.issues.length > 0 ? (
        <Alert severity="error" data-testid="advisory-answer-ordering-warning" sx={{ margin: 2 }}>
          <AlertTitle>{t('advisory:answers.orderingInvalid')}</AlertTitle>
          <Box component="ul" sx={{ marginY: 0 }}>
            {brief.issues.map((issue, index) => (
              <Typography component="li" key={`${issue}-${index}`}>
                {issue}
              </Typography>
            ))}
          </Box>
        </Alert>
      ) : null}
      {brief.model ? (
        <AdvisoryBriefDocument
          key={`${user?.id}:${projectId}`}
          model={brief.model}
          loadSourcePassage={async (request, signal) => {
            let token: string;
            try {
              token = await dispatch(getValidToken()).unwrap();
            } catch {
              return { status: 'denied' };
            }
            if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
            return readAdvisorySourcePassage(request, token, signal);
          }}
        />
      ) : null}
    </Box>
  );

  let content: React.ReactNode;
  let rendersBrief = false;
  let rendersWorkspace = false;

  if (!projectId) {
    // A route param that is not a usable Firestore path segment resolves
    // to the same calm "Not found" the `absent` stage produces — there is
    // no engagement at that address to load, and no listener was
    // registered for it. Deliberately ahead of the `turnStatus` branch: with
    // no projectId no turn can ever have fired.
    content = (
      <AdvisoryCalloutState
        tone="info"
        headingKey="advisory:callout.absent.heading"
        bodyKey="advisory:errors.notFound"
      />
    );
  } else if (usesUnifiedReview && (stage.tag === 'denied' || stage.tag === 'channelFailed')) {
    // Keep initial goal-listener access recovery beside the original decision.
    if (
      !unifiedReview.approved &&
      !unifiedReview.snapshot?.statement.segments.length &&
      ((!unifiedReview.snapshot?.canRefine &&
        !unifiedReview.snapshot?.errors.some(error => error.code === 'clarificationRequired')) ||
        hasReviewPreparationFailure(unifiedReview.snapshot))
    ) {
      content = (
        <AdvisoryUnifiedReviewLive
          review={unifiedReview}
          {...reviewPresentation}
          initialReadFailure={stage.tag === 'denied' ? 'denied' : 'failed'}
          onReloadInitial={retryChannel}
        />
      );
    } else {
      content = (
        <AdvisoryCalloutState
          tone={stage.tag === 'denied' ? 'info' : 'failure'}
          headingKey={
            stage.tag === 'denied'
              ? 'advisory:callout.denied.heading'
              : 'advisory:callout.failed.heading'
          }
          bodyKey={
            stage.tag === 'denied'
              ? 'advisory:errors.permissionDenied'
              : mapAdvisoryErrorCode(stage.errorCode).i18nKey
          }
          onRetry={stage.tag === 'channelFailed' ? retryChannel : undefined}
        />
      );
    }
  } else if (unifiedReview.approved && (unifiedReview.stale || unifiedReview.dirty.length > 0)) {
    // Another tab can finish preparation while our unsaved edits still need
    // acknowledgement. Keep that choice visible before any downstream screen,
    // including while an outstanding save is learning that approval won.
    content = <AdvisoryUnifiedReviewLive review={unifiedReview} {...reviewPresentation} />;
  } else if (usesWorkspace && entry?.goalDoc?.status === 'confirmed' && user?.id) {
    rendersWorkspace = true;
    content = (
      <AdvisoryWorkspaceLive
        key={requestScope}
        userId={user.id}
        projectId={projectId}
        send={send}
        answerReady={
          answerLifecycle.tag === 'completed' &&
          !advisoryAnswers.loadingAnswers &&
          Boolean(liveBrief.model)
        }
        explorationPanel={runView ? <AdvisoryRunProgress embedded view={runView} /> : undefined}
        answerPanel={workspaceRunId =>
          hasAnswerResult ? (
            answerResultPanel(bindWorkspaceBriefToRun(liveBrief, workspaceRunId, tracking.runDoc))
          ) : answerLifecycle.tag === 'active' && answerProgressRows.length ? (
            <AdvisoryAnswerProgress embedded rows={answerProgressRows} />
          ) : undefined
        }
      />
    );
  } else if (liveAnswersEnabled && answerLifecycle.tag === 'active') {
    content = <AdvisoryAnswerProgress rows={answerProgressRows} />;
  } else if (liveAnswersEnabled && hasAnswerResult) {
    rendersBrief = true;
    content = answerResultPanel();
  } else if (
    usesUnifiedReview &&
    (!unifiedReview.approved ||
      (stage.tag !== 'researchingCounterparties' &&
        ((!unifiedReview.preparationComplete && !acknowledgedProfileGaps) ||
          entry?.goalDoc?.status !== 'confirmed')))
  ) {
    content = <AdvisoryUnifiedReviewLive review={unifiedReview} {...reviewPresentation} />;
  } else if (turnStatus === 'error' && !isInlineEditorTurn && !isProfilePreparationError) {
    // This branch precedes the stage switch so that a failed submit
    // renders the failure tone at `/advisory/{id}` rather than the calm
    // "Not found" the `absent` stage would otherwise produce.
    // `errorI18nKey` is already a mapped key — this page never reads a raw
    // error property off anything, and no new path here does.
    // `!isInlineEditorTurn`: a failed removal is reported inline by the editor
    // itself (`handleRemoveKeyQuestion`'s own return value), never by
    // swapping the whole page for a full-screen failure callout — see
    // `isRemovalTurn`'s own comment above.
    content = (
      <AdvisoryCalloutState
        tone="failure"
        headingKey="advisory:callout.failed.heading"
        bodyKey={errorI18nKey ?? 'advisory:errors.unknown'}
        onRetry={retryTurn}
      />
    );
  } else if (
    ((turnInFlight && !isInlineEditorTurn) || stage.tag === 'extracting') &&
    // …unless the counterparty research is what the turn is DOING. Same
    // exception, and the same reason, as `runView` below: the confirm turn now
    // holds its stream open for the whole research — 5-20 minutes per company —
    // so the generic confirm treatment (sized for its measured ~2.8s, and with
    // the leave-note suppressed for that reason) would stand there for twelve
    // minutes saying nothing. That is indistinguishable from a hang, and it is
    // what hid the profile screen entirely: the stage existed, derived
    // correctly, and never got to render because its own turn was in flight.
    // The profile screen IS this turn's progress view.
    stage.tag !== 'researchingCounterparties'
  ) {
    // A turn in flight for this projectId OR the document's own
    // `extracting` status. The OR is what stops the sub-second
    // healthy-but-empty window from surfacing "Not found" during a live
    // turn — the `extracting` half is what makes a reload survive with no
    // derivation change. This condition covers ask, refine AND
    // confirm/classify turns alike — a turn in flight is a turn in flight
    // — but which thinking treatment renders differs: ask/refine share the
    // ~5-25s full-screen "you may leave and come back" treatment (honest
    // for a wait that long and that unpredictable), while confirm/classify
    // get a visibly distinct, leave-note-free treatment sized to their own
    // measured ~2.8s-end-to-end wait — showing the same "leave and come
    // back" promise for both would be dishonest for the short one. Neither
    // branch invents progress: `progressPhase` only ever carries text from
    // a `status` SSE event that has actually arrived (never a
    // timed/fabricated step).
    //
    // An ANALYSIS turn is the third case, and it needs its own treatment for the
    // same reason the other two differ from each other: honesty about the wait.
    // Ask/refine is ~5-25s, confirm/classify ~2.8s — an engagement is ~20 MINUTES
    // and seven turns. The generic full-screen state stood there the whole time
    // saying nothing, which is indistinguishable from a hang; `runView` turns it
    // into a step count that moves.
    //
    // Guarded on `runView` rather than on the request, because progress can only be
    // shown once `analysisStarted` has actually arrived — before that there is no
    // shape to render and the generic state is the honest answer.
    content = runView ? (
      <AdvisoryRunProgress view={runView} progressPhase={progressPhase} />
    ) : isPeriodConfirmTurn ? (
      <AdvisoryThinkingState
        titleKey="advisory:period.confirmingTitle"
        progressPhase={progressPhase}
        suppressLeaveNote
      />
    ) : isConfirmFamilyTurn ? (
      <AdvisoryThinkingState
        titleKey="advisory:thinking.confirm.title"
        progressPhase={progressPhase}
        suppressLeaveNote
      />
    ) : (
      <AdvisoryThinkingState
        titleKey="advisory:thinking.extraction.title"
        progressPhase={progressPhase}
      />
    );
  } else if (
    stage.tag !== 'awaitingCompanyConfirmation' &&
    (stage.tag === 'awaitingConfirmation' ||
      stage.tag === 'refinementExhausted' ||
      stage.tag === 'confirmed' ||
      stage.tag === 'analysisPrepared') &&
    periodView.tag === 'loading'
  ) {
    content = <AdvisoryThinkingState titleKey="advisory:period.loadingTitle" suppressLeaveNote />;
  } else if (
    stage.tag !== 'awaitingCompanyConfirmation' &&
    (stage.tag === 'awaitingConfirmation' ||
      stage.tag === 'refinementExhausted' ||
      stage.tag === 'confirmed' ||
      stage.tag === 'analysisPrepared') &&
    periodView.tag === 'required'
  ) {
    content = (
      <AdvisoryPeriodConfirmationScreen
        period={periodView.period}
        defaulted={periodView.defaulted}
        extractionFailed={periodView.extractionFailed}
        acceptStoredProposal={periodView.acceptStoredProposal}
        onConfirm={handleConfirmPeriod}
      />
    );
  } else if (
    stage.tag !== 'awaitingCompanyConfirmation' &&
    (stage.tag === 'awaitingConfirmation' ||
      stage.tag === 'refinementExhausted' ||
      stage.tag === 'confirmed' ||
      stage.tag === 'analysisPrepared') &&
    (periodView.tag === 'denied' || periodView.tag === 'failed' || periodView.tag === 'invalid')
  ) {
    const periodBodyKey =
      periodView.tag === 'denied'
        ? 'advisory:errors.permissionDenied'
        : periodView.tag === 'invalid'
        ? 'advisory:errors.mapperFailed'
        : mapAdvisoryErrorCode(periodFact.errorCode).i18nKey;
    content = (
      <AdvisoryCalloutState
        tone={periodView.tag === 'denied' ? 'info' : 'failure'}
        headingKey={
          periodView.tag === 'denied'
            ? 'advisory:callout.denied.heading'
            : 'advisory:callout.failed.heading'
        }
        bodyKey={periodBodyKey}
        onRetry={periodView.tag === 'invalid' ? undefined : periodFact.retry}
      />
    );
  } else {
    switch (stage.tag) {
      case 'loading':
        content = (
          <Box sx={{ textAlign: 'center' }}>
            <Skeleton variant="text" width="60%" sx={[heading]} />
            <Skeleton variant="text" width="40%" sx={[meta]} />
          </Box>
        );
        break;
      case 'denied':
        // The default outcome in every deployed environment this milestone —
        // this must read as deliberate, not broken. No retry: it can never
        // succeed against an undeployed Firestore rule.
        content = (
          <AdvisoryCalloutState
            tone="info"
            headingKey="advisory:callout.denied.heading"
            bodyKey="advisory:errors.permissionDenied"
          />
        );
        break;
      case 'channelFailed':
        // Re-registering is idempotent, so this retry needs no guard and
        // no teardown first.
        content = (
          <AdvisoryCalloutState
            tone="failure"
            headingKey="advisory:callout.failed.heading"
            bodyKey={mapAdvisoryErrorCode(stage.errorCode).i18nKey}
            onRetry={retryChannel}
          />
        );
        break;
      case 'absent':
        // `absent` is distinguished from every failure tag, so this
        // renders a calm info state, not a bespoke 404 page.
        content = (
          <AdvisoryCalloutState
            tone="info"
            headingKey="advisory:callout.absent.heading"
            bodyKey="advisory:errors.notFound"
          />
        );
        break;
      case 'reviewing':
        content = <AdvisoryUnifiedReviewLive review={unifiedReview} {...reviewPresentation} />;
        break;
      case 'awaitingCompanyConfirmation':
        // Ordered before the goal branches, matching `deriveAdvisoryStage`. The
        // company is read on the same first turn as the goal and is asked about
        // first, because the goal the user is about to confirm is a goal ABOUT
        // this company — reviewing it while the subject is still unsettled asks
        // them to approve half a sentence.
        content = (
          <AdvisoryCompanyScreen
            stage={stage}
            busy={turnInFlight}
            onConfirm={handleConfirmCompany}
          />
        );
        break;
      case 'awaitingCounterpartyConfirmation':
        // After the company and before the fork. The engagement type is already
        // known here — it is what put us in this stage — and the question set it
        // gates has not been locked yet.
        content = (
          <AdvisoryCounterpartiesScreen
            stage={stage}
            busy={turnInFlight}
            onConfirm={handleConfirmCounterparties}
          />
        );
        break;
      case 'researchingCounterparties':
        // After the counterparty confirmation and before the fork. The research
        // fired on that confirmation and the analysis waits for it, so this is
        // where the wait becomes visible instead of a silent wizard.
        content = (
          <>
            <AdvisoryCounterpartyProfilesScreen
              stage={stage}
              busy={turnInFlight}
              onRetry={handleRetryCounterpartyProfile}
              onContinue={handleContinuePastProfiles}
            />
            {approvedReview && (
              <Box sx={[contentColumn, { px: 2.5, pb: 3 }]}>
                <AdvisoryReviewRecoveryControls
                  review={unifiedReview}
                  busy={turnInFlight}
                  variant="profiles"
                />
              </Box>
            )}
          </>
        );
        break;
      case 'awaitingConfirmation':
      case 'refinementExhausted': {
        // Raw current goal text — deliberately NOT truncateGoalTitle, which
        // exists for the 80-character list-row title and would elide the
        // very sentence the user needs to read in full. Do not "unify" the
        // two call sites.
        //
        // Prefers this browser tab's own live SSE `goal` state over the
        // Firestore-fed `entry?.goalDoc` — the goal document is written
        // mid-stream (useSupervisorTurn.ts's module header), so a
        // listener snapshot racing the same turn can observe a half-written
        // document the SSE frame that already reached this hook instance
        // has already superseded. Falls back to the Firestore-derived text
        // when no SSE `goal` event has landed in this tab (e.g. arriving at
        // this route via a bookmark or the engagements list, with no live
        // turn ever fired here) — that fallback is the pre-existing
        // `onSnapshot` listener this page already runs unconditionally, not
        // a new reload-recovery read.
        const goalText = sseGoal?.goal ?? entry?.goalDoc?.goal?.[0]?.goal ?? null;
        content = (
          <AdvisoryGoalReviewScreen
            stage={stage}
            goalText={goalText}
            onRefine={handleRefine}
            onConfirm={handleConfirm}
          />
        );
        break;
      }
      // RELOAD: `turnInFlight` is false on a fresh mount, so a run that is still
      // going lands here. `runView` is document-sourced then — counters, no rows —
      // which is exactly why `AdvisoryRunProgress` renders a view rather than a
      // stream: the same component covers both without knowing which it got.
      //
      // Ahead of the wizard steps on purpose. A reload during a run used to drop the
      // user back onto the evidence dropzone as though nothing were happening, with
      // a Start button that would re-attach to a run they could not see.
      case 'analysisRunning':
      case 'analysisComplete':
      case 'analysisPartial':
      case 'analysisFailed':
        content = runView ? (
          <AdvisoryRunProgress view={runView} />
        ) : (
          // Unreachable: `runViewFromStage` returns a view for exactly these four
          // tags. Kept so a future fifth analysis tag cannot render blank.
          <AdvisoryCalloutState
            tone="info"
            headingKey="advisory:callout.absent.heading"
            bodyKey="advisory:errors.unknown"
          />
        );
        break;
      // `analysisPrepared` is deliberately NOT here: a conversation exists and
      // documents may be uploaded, but nothing is running, so the wizard is still
      // the right thing to show. `runViewFromStage` returns null for it.
      case 'analysisPrepared':
      case 'confirmed': {
        // `extracting` is deliberately absent from this switch: the branch
        // above already claims it unconditionally, and TypeScript's own
        // control-flow narrowing on the `else if` above excludes it from
        // `stage.tag`'s type here, so a case for it would be a compile
        // error, not dead code.
        //
        // `path`/`step` only ever select between screens the server-known
        // `confirmed` stage itself cannot distinguish (which counted
        // wizard step to show). They are never
        // trusted as-is — an absent, unrecognised, or valid-but-impossible
        // combination (e.g. `path=express&step=criteria`) all fall through
        // `stepPosition` returning `null` and reconcile to the fork screen,
        // never to a blank step.
        const stepPos = path && step ? stepPosition(step, path) : null;

        const forkScreen = (
          <AdvisoryForkScreen
            engagement={displayedEngagement}
            onChoosePath={chosenPath => {
              if (chosenPath === 'express') {
                setWizardParams({ path: 'express', step: 'evidence' });
              } else {
                setWizardParams({ path: 'customize', step: 'keyQuestions' });
              }
            }}
          />
        );

        // A falsy `projectId` cannot occur on the `/advisory/:projectId`
        // route, but it shares the fork fallback rather than a bare `else if`
        // so that no path through this case can leave `content` unassigned —
        // "never to a blank step" above is then structurally true, not just
        // true for the reachable inputs.
        if (!stepPos || !path || !step || !projectId) {
          content = forkScreen;
        } else if (step === 'keyQuestions') {
          // The key-question editor renders its OWN AdvisoryEngagementShell
          // — do not wrap it in a second one. Confirm navigates only: it
          // writes nothing and dispatches no advisory action. Back clears
          // the search string entirely rather than inventing a "clear the
          // step" API on useAdvisoryWizardParams — with no earlier
          // customize step left to go back to (the `criteria` step was
          // removed), the `!stepPos || !path || !step` reconciliation
          // above then resolves the next render to the fork screen,
          // exactly as intended.
          content = (
            <AdvisoryKeyQuestionEditor
              projectId={projectId}
              keyQuestions={displayedKeyQuestions}
              channelState={effectiveKeyQuestionsChannelState}
              channelErrorCode={keyQuestionsChannel.errorCode}
              orphaned={sseOrphaned}
              onRemove={handleRemoveKeyQuestion}
              onAdd={handleAddKeyQuestion}
              proposals={sseProposals}
              onAccept={handleAcceptQuestion}
              onRetryChannel={retryChannel}
              onBack={() => navigate(location.pathname, { replace: true })}
              onConfirm={() =>
                setWizardParams({ path: 'customize', step: 'evidence' }, { replace: true })
              }
            />
          );
        } else if (step === 'evidence') {
          // `evidence` is the last step. Its terminal action now starts the S-05
          // engagement (`handleStartAnalysis` above) rather than handing off to
          // an execution flow that did not exist when this branch was written.
          // The step body renders its OWN AdvisoryEngagementShell — do not wrap
          // it in a second one, mirroring the key-question branch above.
          content = (
            <AdvisoryEvidenceScreen
              path={path}
              documents={documents}
              onStart={handleStartAnalysis}
              onBack={() =>
                path === 'customize'
                  ? setWizardParams({ path: 'customize', step: 'keyQuestions' }, { replace: true })
                  : navigate(location.pathname, { replace: true })
              }
            />
          );
        } else {
          // The criteria case that used to live here is gone, and so is
          // the step value that used to name it — the `AdvisoryWizardStep`
          // union no longer carries it at all. A stale bookmark, a
          // hand-typed URL or a pre-existing browser history entry can
          // still supply that now-unrecognized step value through
          // `stepPosition` today. Without this fallback that value would
          // fall through a bare trailing `else` straight into the evidence
          // screen — the URL claiming one step while the app silently
          // shows another, exactly the failure this branch (and the
          // `path`/`step` selection rule above) is meant to prevent.
          // Anything that reaches here reconciles to the fork instead.
          content = forkScreen;
        }
        break;
      }
      case 'extractionFailed':
        // The goal document's own `status: 'failed'` is a terminal outcome
        // with no retry, visually distinct from `denied` (info tone) and
        // from `channelFailed` (failure tone WITH a retry button).
        content = (
          <AdvisoryCalloutState
            tone="failure"
            headingKey="advisory:callout.extractionFailed.heading"
            bodyKey="advisory:callout.extractionFailed.body"
          />
        );
        break;
    }
  }

  return (
    <Box
      sx={[
        pageSurface,
        {
          display: 'flex',
          justifyContent: 'center',
          alignItems: rendersBrief || rendersWorkspace ? 'stretch' : 'center',
          p: rendersBrief || rendersWorkspace ? 0 : 2.5,
        },
      ]}
    >
      <Box sx={rendersBrief || rendersWorkspace ? { width: '100%' } : [contentColumn]}>
        {usesUnifiedReview && !rendersWorkspace && (
          <AdvisoryEngineLabel
            engine={
              unifiedReview.snapshot ? unifiedReview.snapshot.engine : submittedQuery.current.engine
            }
          />
        )}
        {intakeStorageError === requestScope && (
          <Alert
            severity="error"
            action={
              <Button onClick={() => setIntakeAttempt(value => value + 1)}>
                {t('advisory:engine.retry')}
              </Button>
            }
          >
            {t('advisory:engine.storageFailed')}
          </Alert>
        )}
        {content}
      </Box>
    </Box>
  );
}

export default AdvisoryEngagementPage;
